import { useEffect, useRef } from 'react';

import { Box } from '@rover/kibble/core';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

import { Props, REDUCED_MOTION_QUERY, resolveVideoSource } from './VideoPlayer.common';

/**
 * Plays a video inline using the browser's own `<video>` controls.
 *
 * Native controls are deliberate: they are keyboard-operable and screen-reader
 * labelled for free, which a bespoke control bar would have to re-earn. Add one
 * only if design explicitly asks.
 *
 * @example
 * <VideoPlayer
 *   sources={[introVideo.videoUrl]}
 *   posterUrl={introVideo.thumbnailUrl ?? undefined}
 *   description={t`Sitter introduction video`}
 * />
 */
const VideoPlayer = ({
  sources,
  description,
  posterUrl,
  autoPlay = false,
  loop = false,
  onPlay,
  onEnded,
  updateVideoDuration,
  maxHeight,
}: Props): JSX.Element | null => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const prefersReducedMotion = useMatchMedia(REDUCED_MOTION_QUERY);

  // `useMatchMedia` reports `undefined` until its effect runs on the client, so
  // motion is allowed only once the preference has been positively observed.
  // Anything else fails closed to the calmer behaviour.
  const motionAllowed = prefersReducedMotion === false;
  const shouldAutoPlay = autoPlay && motionAllowed;
  const shouldLoop = loop && motionAllowed;
  const sourceKey = sources.join('|');

  useEffect(() => {
    if (!videoRef.current || !shouldAutoPlay) {
      return;
    }

    videoRef.current.muted = true;

    // The reduced-motion preference is unknown for the first render, so the
    // element is created without an `autoplay` attribute and only gains one
    // afterwards. Browsers do not reliably act on that late attribute, so
    // playback is started imperatively instead — as the native player does.
    // A rejection means the browser declined to autoplay, which is its right.
    videoRef.current.play().catch(() => undefined);
    // `sourceKey` is a dependency because a source change mounts a fresh
    // element, which needs starting again.
  }, [shouldAutoPlay, sourceKey]);

  useEffect(() => {
    // Captured now rather than read in the cleanup: by the time cleanup runs on
    // a source change the ref already points at the replacement element, so the
    // one being discarded would never be paused.
    const video = videoRef.current;

    // Detaching a `<video>` does not reliably stop it — engines have kept
    // detached media playing until collection — and React does nothing to the
    // element beyond unmounting the node. Left alone that is audio with no
    // visible player, which gets reported as a haunting rather than a bug.
    // Pausing is enough to stop sound; the element is being discarded either
    // way, so there is no `load()` here to tear down a resource selection that
    // would only start over.
    return () => video?.pause();
  }, [sourceKey]);

  if (!resolveVideoSource(sources)) {
    return null;
  }

  return (
    <Box
      width="100%"
      borderRadius="primary"
      // Centred only when capped, where the video may be narrower than this box.
      sx={
        maxHeight
          ? { overflow: 'hidden', display: 'flex', justifyContent: 'center' }
          : { overflow: 'hidden' }
      }
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- captions are out of scope for the intro videos MVP; `description` carries the accessible name */}
      <video
        key={sourceKey}
        ref={videoRef}
        aria-label={description}
        controls
        preload="metadata"
        playsInline
        poster={posterUrl}
        // `autoplay` and `muted` are deliberately absent: the effect above owns
        // both, so muting cannot be undone by a later render.
        loop={shouldLoop}
        onPlay={onPlay}
        onEnded={onEnded}
        // The duration is `NaN` until the browser has read the file's metadata,
        // so it can only be reported from here — not when the element mounts.
        onLoadedMetadata={(event): void => {
          const { duration } = event.currentTarget;

          if (Number.isFinite(duration)) {
            updateVideoDuration?.(duration);
          }
        }}
        style={
          maxHeight
            ? {
                display: 'block',
                width: 'auto',
                maxWidth: '100%',
                maxHeight,
                // The wrapper is full-width, so under the cap its corners sit
                // outside a narrower video's edges. Inheriting puts them back
                // on the video itself.
                borderRadius: 'inherit',
              }
            : { display: 'block', width: '100%' }
        }
        data-testid="video-player"
      >
        {sources.map((source) => (
          <source key={source} src={source} />
        ))}
      </video>
    </Box>
  );
};

export default VideoPlayer;
